import React from 'react';
import { motion } from 'framer-motion';
import { BlurText } from './animations/BlurText';
import { Aurora } from './animations/Aurora';
import { Particles } from './animations/Particles';
import { Constellation } from './animations/Constellation';
import { CircuitGrid } from './animations/CircuitGrid';
import { Beams } from './animations/Beams';
import { Threads } from './animations/Threads';
import { OrbitField } from './animations/OrbitField';
import { Spotlight } from './animations/Spotlight';
import { GlowOrbs } from './animations/GlowOrbs';
export type PageHeroBg = 'none' | 'constellation' | 'circuit' | 'particles' | 'aurora' | 'beams' | 'threads' | 'orbit' | 'spotlight' | 'glow-orbs';
interface PageHeroProps {
  eyebrow: string;
  title: string;
  titleMuted?: string;
  description: string;
  bg: PageHeroBg;
  children?: ReactNode;
}
const bgRenderers: Record<PageHeroBg, () => ReactNode> = {
  none: () => null,
  constellation: () => <>
      <Aurora intensity={0.18} data-id="element-1121" />
      <Constellation nodes={70} linkDistance={0.18} data-id="element-1122" />
    </>,
  circuit: () => <>
      <Aurora intensity={0.15} data-id="element-1123" />
      <CircuitGrid rows={6} cols={10} data-id="element-1124" />
    </>,
  particles: () => <>
      <Aurora intensity={0.2} data-id="element-1125" />
      <Particles count={90} data-id="element-1126" />
    </>,
  aurora: () => <Aurora intensity={0.35} data-id="element-1127" />,
  beams: () => <>
      <Aurora intensity={0.15} data-id="element-1128" />
      <Beams count={7} data-id="element-1129" />
    </>,
  threads: () => <>
      <Aurora intensity={0.18} data-id="element-1130" />
      <Threads count={8} data-id="element-1131" />
    </>,
  orbit: () => <>
      <Aurora intensity={0.2} data-id="element-1132" />
      <OrbitField center={{
      x: 50,
      y: 50
    }} size={500} count={4} data-id="element-1133" />
    </>,
  spotlight: () => <>
      <Aurora intensity={0.2} data-id="element-1134" />
      <Spotlight size={600} data-id="element-1135" />
    </>,
  'glow-orbs': () => <GlowOrbs count={6} data-id="element-1136" />
};
export function PageHero({
  eyebrow,
  title,
  titleMuted,
  description,
  bg,
  children
}: PageHeroProps) {
  return <section className="relative w-full pt-40 pb-24 overflow-hidden" data-id="element-1137">
      {bg !== 'none' && <>
          <div className="absolute inset-0 pointer-events-none" data-id="element-1138">
            {bgRenderers[bg]()}
          </div>
          <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 70% 60% at 20% 30%, rgba(5,6,8,0.35) 20%, rgba(5,6,8,0.85) 80%, #050608 100%)'
      }} data-id="element-1139" />
        </>}

      {/* Asymmetric editorial composition. Eyebrow + headline left-aligned in
            a narrower column; description anchored to a wider line-length on
            the right. No centered hero block. */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1140">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="mb-10" data-id="element-1141">
          <span className="inline-flex items-center gap-3 text-[#1a8fd4] text-[11px] font-mono uppercase tracking-[0.3em]" data-id="element-1142">
            <span className="h-px w-8 bg-[#1a8fd4]/40" aria-hidden="true" data-id="element-1143" />
            {eyebrow}
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end" data-id="element-1144">
          <h1 className="lg:col-span-8 text-5xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.03em] leading-[1] text-white" data-id="element-1145">
            <BlurText text={title} splitBy="word" delay={0.1} data-id="element-1146" />
            {titleMuted && <>
                <br data-id="element-1147" />
                <span className="text-white/40" data-id="element-1148">
                  <BlurText text={titleMuted} splitBy="word" delay={0.35} data-id="element-1149" />
                </span>
              </>}
          </h1>

          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.5
        }} className="lg:col-span-4 text-base md:text-lg text-white/65 leading-relaxed" data-id="element-1150">
            {description}
          </motion.p>
        </div>

        {children && <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.75
      }} className="mt-12" data-id="element-1151">
            {children}
          </motion.div>}
      </div>
    </section>;
}